// src/components/features/journey-planning/ResultsPage/JourneyPlanResultsPage.tsx
import { useState } from 'preact/hooks';
import { LoadingSpinner } from '../../../base/LoadingSpinner/LoadingSpinner';
import { PagePadding, ResultsPage } from '../../../widget/Pages/Pages';
import { JourneyPlanForm } from '../Form/PlanForm/JourneyPlanForm';
import { JourneyPlanResultsList } from '../ResultsList/JourneyPlanResultsList';
import { EmptyState } from '../../../base/EmptyState/EmptyState';
// Type
import type { JourneyPlan } from '../../../../arcticapi/types';
import type { JourneyPlanFormModel } from '../Form/PlanForm/JourneyPlanForm.types';

interface JourneyPlanResultsPageProps {
  form: JourneyPlanFormModel;
  timingLabel: string;
  plans: JourneyPlan[];
  error?: Error | null;
  onSelectPlan: (index: number) => void;
  onShowEarlierJourneys?: () => void;
  onShowLaterJourneys?: () => void;
  searchedDate?: string | null;
}

/**
 * Journey planning results page including summary header, states and result list.
 */
export function JourneyPlanResultsPage({
  form,
  timingLabel,
  plans,
  error = null,
  onSelectPlan,
  onShowEarlierJourneys,
  onShowLaterJourneys,
  searchedDate = null,
}: JourneyPlanResultsPageProps) {
  // Results are hidden while filters are edited: they'd be stale, and the
  // expanded options would otherwise squeeze the list into a nested scroll.
  const [isEditingFilters, setIsEditingFilters] = useState(false);
  const hasInlineState =
    !form.submit.loading && (error !== null || plans.length === 0);

  return (
    <ResultsPage padding="flush">
      <JourneyPlanForm
        form={form}
        mode="results"
        timingLabel={timingLabel}
        onAdvancedJourneyOptionsToggle={setIsEditingFilters}
      />
      {!isEditingFilters && (
        <>
          {form.submit.loading && (
            <PagePadding>
              <LoadingSpinner />
            </PagePadding>
          )}
          {hasInlineState ? (
            <PagePadding>
              {error ? (
                <EmptyState title="No journeys found" message={error.message} />
              ) : (
                <EmptyState
                  title="No journeys found"
                  message="Try a different pair of stops"
                />
              )}
            </PagePadding>
          ) : (
            <JourneyPlanResultsList
              plans={plans}
              aria-label="Journey results"
              onSelectPlan={onSelectPlan}
              onShowEarlierJourneys={onShowEarlierJourneys}
              onShowLaterJourneys={onShowLaterJourneys}
              searchedDate={searchedDate}
            />
          )}
        </>
      )}
    </ResultsPage>
  );
}
